import { useEffect, useState, useCallback } from "react";
import PageHeader from "src/core/ui/PageHeader";
import Footer from "src/core/ui/helpers/Footer";
import ContentCardsContainer, { ContentCard } from "src/core/ui/ContentCard";
import { getUserToken } from "src/core/api/apiRequests";
import useRequest from "src/core/api/useRequest";
import useCache from "src/core/api/cache/useCache";
import {
  getAuthToken,
  getRefreshToken,
  getExpiryMs,
  isInitialized,
  refreshIfNeeded,
  setTokens,
  clearTokens,
  on as onTokenEvent,
} from "src/shared/auth/singletonToken";

export function Kalle3() {
  const [tokenResult, setRequest, abort, loading] = useRequest();

  const [logs, setLogs] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState({
    authToken: null,
    refreshToken: null,
    expMs: null,
    initialized: false,
  });

  const addLog = useCallback((msg: string) => {
    setLogs((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev]);
  }, []);

  // keep snapshot in sync with singleton
  const updateSnapshot = useCallback(() => {
    setSnapshot({
      authToken: getAuthToken(),
      refreshToken: getRefreshToken(),
      expMs: getExpiryMs(),
      initialized: isInitialized(),
    });
  }, []);

  // watch singleton events
  useEffect(() => {
    updateSnapshot();
    const offUpdated = onTokenEvent("updated", () => {
      addLog("Token updated");
      updateSnapshot();
    });
    const offLoggedOut = onTokenEvent("loggedOut", () => {
      addLog("Logged out");
      updateSnapshot();
    });
    const offRefreshing = onTokenEvent("refreshing", () => {
      addLog("Refreshing token (in background)...");
      updateSnapshot();
    });
    return () => {
      offUpdated();
      offLoggedOut();
      offRefreshing();
    };
  }, [addLog, updateSnapshot]);

  // when login request resolves, log
  useEffect(() => {
    if (tokenResult?.access_token) {
      addLog("Login succeeded, token received");
    }
  }, [tokenResult, addLog]);

  // actions
  const loginHardcoded = () => {
    abort();
    addLog("Sending login request with hardcoded creds...");
    setRequest(getUserToken("kallek-test", "welcome"));
  };

  const logout = () => {
    clearTokens();
    addLog("Manual logout called");
  };

  const forceRefresh = async () => {
    addLog("Force refresh triggered");
    await refreshIfNeeded({ force: true, reason: "manual" });
    updateSnapshot();
  };

  const forceExpireSoon = () => {
    const now = Date.now();
    setTokens({
      access_token: getAuthToken(),
      refresh_token: getRefreshToken(),
      expMs: now + 10_000, // expire in 10s
      expires_in: 10,
    });
    addLog("Expiry forced to 10s from now");
    updateSnapshot();
  };

  return (
    <>
      <PageHeader title="Kalles Auth Test" />
      <ContentCardsContainer>
        {/* Card: Token snapshot + logs */}
        <ContentCard cardId="Logs">
          <h3>Current Token Snapshot</h3>
          <pre style={{ background: "#000", padding: "8px", fontSize: "0.8rem" }}>
            {JSON.stringify(snapshot, null, 2)}
          </pre>
          <h3>Event Log</h3>
          <div
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              border: "1px solid #ddd",
              padding: "6px",
              fontSize: "0.8rem",
              background: "#000",
            }}
          >
            {logs.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        </ContentCard>

        {/* Card: Controls */}
        <ContentCard cardId="Controls">
          <button disabled={loading} onClick={loginHardcoded}>
            {loading ? "Logging in..." : "Login (hardcoded user)"}
          </button>
          <button onClick={logout}>Logout</button>
          <button onClick={forceRefresh}>Force Refresh</button>
          <button onClick={forceExpireSoon}>Force Expire Soon</button>
        </ContentCard>
      </ContentCardsContainer>
      <Footer />
    </>
  );
}

export default Kalle3;
